import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";

const EXPENSE_CATEGORIES = [
  { value: "transport", label: "Transport" },
  { value: "carburant", label: "Carburant" },
  { value: "loyer", label: "Loyer" },
  { value: "internet", label: "Internet" },
  { value: "électricité", label: "Électricité" },
  { value: "fournitures", label: "Fournitures" },
  { value: "maintenance", label: "Maintenance" },
  { value: "salaire", label: "Salaire" },
  { value: "autre", label: "Autre" },
];

const initialForm = {
  category: "",
  amount: "",
  currency: "USD",
  beneficiary_name: "",
  description: "",
  expense_date: new Date().toISOString().slice(0, 10),
};

const ExpenseForm = ({ onCreated }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const assignedAgencyLabel = useMemo(() => {
    if (!user?.agency_id) return "";
    return [
      user?.agency_name || "",
      user?.city || user?.location_city || "",
      user?.country || user?.location_country || "",
    ]
      .filter(Boolean)
      .join(" • ");
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const { category, amount, currency, beneficiary_name, description, expense_date } = form;

    if (!user?.agency_id) {
      enqueueSnackbar("Votre compte n’a pas d’agence assignée", {
        variant: "error",
      });
      return;
    }

    if (!category || !amount || !currency) {
      enqueueSnackbar("Catégorie, montant et devise sont requis", {
        variant: "warning",
      });
      return;
    }

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      enqueueSnackbar("Montant invalide", { variant: "warning" });
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        agency_id: Number(user.agency_id),
        category,
        amount: numericAmount,
        currency: String(currency).trim().toUpperCase(),
        beneficiary_name: beneficiary_name.trim() || null,
        description: description.trim() || null,
        expense_date: expense_date || null,
        status: "validated",
      };

      const res = await api.post("/expenses", payload);

      enqueueSnackbar("Dépense enregistrée avec succès", {
        variant: "success",
      });

      setForm(initialForm);

      if (typeof onCreated === "function") {
        onCreated(res.data);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error || "Échec de l'enregistrement de la dépense";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
        Nouvelle dépense
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Agence :</strong>{" "}
        {assignedAgencyLabel || `Agence #${user?.agency_id || "non définie"}`}
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Catégorie"
            name="category"
            value={form.category}
            onChange={handleChange}
          >
            {EXPENSE_CATEGORIES.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Montant"
            name="amount"
            type="number"
            value={form.amount}
            onChange={handleChange}
            inputProps={{ min: 0, step: "0.01" }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="Devise"
            name="currency"
            value={form.currency}
            onChange={handleChange}
          >
            <MenuItem value="USD">USD</MenuItem>
            <MenuItem value="CDF">CDF</MenuItem>
            <MenuItem value="RMB">RMB</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Date de la dépense"
            name="expense_date"
            type="date"
            value={form.expense_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Bénéficiaire"
            name="beneficiary_name"
            value={form.beneficiary_name}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Description"
            name="description"
            value={form.description}
            onChange={handleChange}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </Box>
    </Paper>
  );
};

export default ExpenseForm;